import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  BorderColorOutlined,
  CheckCircleOutline,
  KeyboardReturnOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";
import {
  getErrorStatusCode,
  handleErrorMessage,
  openWarningNotification,
} from "helpers/methods";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  acknowledgeHandoff,
  advanceInPersonSession,
  endInPersonSession,
  selectInPersonSession,
} from "redux/inPersonSessionSlice";
import CustomInput from "global/components/CustomInput/CustomInput";
import { ConfirmModal } from "global/components/AppModal";
import { InPersonSessionResponse } from "models/interfaces";
import { fetchSignRequest } from "screens/SignRequest/SignRequestService";
import {
  addInPersonSignerEmail,
  advanceInPersonHostLinkSession,
  advanceInPersonSession as advanceInPersonSessionApi,
  fetchSignerConsent,
  recordSignerConsent,
  returnInPersonHostLinkSession,
  returnInPersonSession,
} from "./InPersonSigningService";
import { isInPersonSessionSigner } from "./InPersonSigningHelper";
import InPersonHandoffDialog from "./InPersonHandoffDialog";
import inPersonStyles from "./InPersonSigning.styles";

/**
 * The three outcomes the "return device" card can land on once a signer
 * finishes and there's no error to recover from — see `runNext`.
 */
type ReturnDeviceBranch = "nextSigner" | "noMoreInPerson" | "completed";

/**
 * Focus-locked hand-off shell that wraps the existing token-scoped signing
 * surface for an in-person session. It renders, in order:
 *  1. a hand-off interstitial (until the host taps continue),
 *  2. the signing surface with a floating, always-visible "Return to host",
 *  3. a completion screen once the signer finishes (drives the next-signer loop),
 *  4. a session summary when no in-person signer remains.
 *
 * The signing UI is unchanged — this only overlays chrome and advances the
 * session via the host endpoints. See the In-Person Signing hand-off spec.
 */
const InPersonSessionShell = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();
  const styles = inPersonStyles(theme);
  const dispatch = useAppDispatch();
  const session = useAppSelector(selectInPersonSession);
  const location = useLocation();

  const [busy, setBusy] = useState<boolean>(false);
  // The present signer's e-signing consent, ticked on the hand-off card before
  // the document is revealed. Seeded from the backend so a reload never asks
  // for a consent already given.
  const [signerConsented, setSignerConsented] = useState<boolean>(false);
  // Set once the just-finished signer's turn has been posted to the backend
  // and we know which of the three outcomes the "return device" card shows.
  // `pendingNextSession` only has a value for the "nextSigner" branch — it's
  // what "Start for next signer" advances the session with.
  const [returnDeviceBranch, setReturnDeviceBranch] =
    useState<ReturnDeviceBranch | null>(null);
  const [pendingNextSession, setPendingNextSession] =
    useState<InPersonSessionResponse | null>(null);
  // Optional "email me a copy" field on the completion card — always shown,
  // read (and posted, if filled in) only when the signer moves on.
  const [copyEmail, setCopyEmail] = useState<string>("");
  const [copyError, setCopyError] = useState<string>("");
  const [captureOpen, setCaptureOpen] = useState<boolean>(false);
  const [captureMsg, setCaptureMsg] = useState<string>("");
  // Browser back/forward mid-turn is caught by `history.block` below and
  // answered with this confirm instead of silently abandoning the signer.
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState<boolean>(false);
  const [leaveBusy, setLeaveBusy] = useState<boolean>(false);
  const pendingLocationRef = useRef<{ pathname: string; search: string } | null>(
    null,
  );
  // Skips the "leave site?" prompt for our own hand-off navigation
  // (`navigateToSigner`'s full-page load) — only an actual tab close/refresh/
  // URL change should trigger it.
  const suppressUnloadRef = useRef<boolean>(false);

  // ParticipantScreen appends `signed=true` on a successful sign — the signal
  // that this signer finished and we should offer the next / summary.
  const isSignedView =
    new URLSearchParams(location.search).get("signed") === "true";
  const requestId = session.requestId;
  const requestToken = session.requestToken;
  const requestKey = session.requestKey;
  // Set only for a session launched from the public host-link page — every
  // next/return call in that session must ride the token, not the login.
  const hostToken = session.hostToken;
  const hostKey = session.hostKey;

  useEffect(() => {
    if (!requestToken) return;
    let stale = false;
    fetchSignerConsent(requestToken, requestKey)
      .then((status) => {
        if (!stale && status?.signerConsented) setSignerConsented(true);
      })
      // Not worth blocking the hand-off card on: an unread status just means
      // the signer ticks the box, and re-accepting is harmless.
      .catch(() => undefined);
    return () => {
      stale = true;
    };
  }, [requestToken, requestKey]);

  // Guards the back/forward and tab-close prompts below. Excludes the
  // return-device interstitial (`returnDeviceBranch` set): by then the signer
  // who held the device already finished or was already returned, so there is
  // no in-progress turn left to abandon — see `abandonSession`.
  const guardNav = session.active && !returnDeviceBranch;

  // Intercepts the browser back/forward button (a `POP` transition) mid-turn
  // and swaps it for `leaveConfirmOpen` instead of silently abandoning the
  // signer. Every other navigation (our own `history.push` calls, e.g. after
  // a successful sign) is a `PUSH`/`REPLACE` and passes straight through.
  useEffect(() => {
    if (!guardNav) return undefined;
    const unblock = history.block((nextLocation, action) => {
      if (action !== "POP") return undefined;
      pendingLocationRef.current = {
        pathname: nextLocation.pathname,
        search: nextLocation.search,
      };
      setLeaveConfirmOpen(true);
      return false;
    });
    return unblock;
  }, [guardNav]);

  // Tab close/refresh/typing a new URL can't be intercepted with our own
  // modal — browsers only allow their own native "leave site?" prompt here,
  // and give no reliable hook to await the abandon call before the page
  // unloads. This is best-effort friction only; the backend's own session TTL
  // is what actually clears the lock if the signer just closes the tab.
  useEffect(() => {
    if (!guardNav) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (suppressUnloadRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () =>
      window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [guardNav]);

  // A hand-off URL without an active session (e.g. tampered link) has no data
  // to drive the loop — fall back to the bare signing surface.
  if (!session.active) return <>{children}</>;

  const goToRequest = () => {
    // The view-request/sent-requests destinations are both behind login — a
    // host-link session has no logged-in user to land there, so send them to
    // sign in instead once the session is done with them.
    if (hostToken) {
      history.push(viewpaths.loginViewPath);
    } else if (requestId) {
      history.push(`${viewpaths.viewRequestViewPath}/${requestId}`);
    } else {
      history.push(viewpaths.sentSignRequestsViewPath);
    }
  };

  const navigateToSigner = (requestToken: string, requestKey?: string) => {
    suppressUnloadRef.current = true;
    window.location.assign(
      `${viewpaths.doSign}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(
        requestKey || "",
      )}&inperson=1`,
    );
  };

  // The signer's own e-signing consent is recorded before the document is
  // revealed — blocking, so a failed post keeps them on the hand-off card
  // rather than opening a document their signing would 406 on anyway. Posted
  // against their own `requestToken`, not the host session.
  const handleContinueHandoff = async () => {
    if (!signerConsented) return;
    setBusy(true);
    try {
      await recordSignerConsent(requestToken, requestKey);
      dispatch(acknowledgeHandoff());
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setBusy(false);
    }
  };

  // Abandons the signer currently holding the device — the one call behind
  // both the explicit "Return to host" button and a confirmed back/tab-close
  // exit. Never blocks the exit it's part of: a failed unlock call still
  // clears the client-side lock and falls through, it just also warns.
  const abandonSession = async () => {
    try {
      if (hostToken) {
        await returnInPersonHostLinkSession(
          hostToken,
          hostKey,
          session.signerRef,
          true,
        );
      } else if (requestId) {
        await returnInPersonSession(requestId, session.signerRef, true);
      }
    } catch (error: any) {
      // Log AND warn the host so a stuck backend session lock (the 409
      // "session in progress" guard) is discoverable rather than silently
      // surfacing as a failed launch later.
      console.error("in-person: end-session call failed", error);
      openWarningNotification(strings.inPerson.returnFailed);
    } finally {
      dispatch(endInPersonSession());
    }
  };

  const handleReturnToHost = async () => {
    setBusy(true);
    await abandonSession();
    setBusy(false);
    goToRequest();
  };

  // The confirm answered "yes" from the back/forward or beforeunload guard —
  // ends the turn the same way "Return to host" does, then replays the
  // navigation the browser's back button was trying to make. Replaying via
  // `push` (not `history.go`) is deliberate: a blocked `POP` has already been
  // reverted in place by the history library, so `go(-1)` from here would
  // walk one entry too far.
  const handleConfirmLeave = async () => {
    const pending = pendingLocationRef.current;
    setLeaveBusy(true);
    await abandonSession();
    setLeaveBusy(false);
    setLeaveConfirmOpen(false);
    pendingLocationRef.current = null;
    if (pending) history.push(`${pending.pathname}${pending.search}`);
  };

  const handleCancelLeave = () => {
    setLeaveConfirmOpen(false);
    pendingLocationRef.current = null;
  };

  // Advance to the next in-person signer, optionally passing walk-in details
  // the host captured for a blank placeholder. Does not navigate itself —
  // it only resolves which of the three "return device" branches applies,
  // so the signer sees that card (and hands the device back) before the next
  // signer's turn actually starts. See `handleStartNextSigner` / `handleDone`.
  // Returns the raw response so a capture submit (which already has the
  // session it needs) can navigate immediately instead of waiting for a
  // second click on the card this also puts up.
  const runNext = async (details?: { name: string; email: string }) => {
    const response = hostToken
      ? await advanceInPersonHostLinkSession(hostToken, hostKey, details)
      : await advanceInPersonSessionApi(requestId, details);
    // A session resolved to an emailed signer is not the host's to run —
    // treat it exactly like "no in-person signer left" rather than opening
    // that signer's document on this device. See `isInPersonSessionSigner`.
    const wrongSigner = Boolean(
      response?.requestToken && !isInPersonSessionSigner(response),
    );
    if (wrongSigner) {
      console.error(
        "in-person: session/next resolved to a non in-person signer",
        response?.signerRef,
      );
    }
    if (wrongSigner || response?.sessionComplete || !response?.requestToken) {
      // No in-person signer left, but that alone doesn't say whether the
      // envelope itself is done — other, remote signers may still be
      // pending. Re-check the envelope this signer just finished.
      let isRequestComplete = false;
      try {
        const fullRequest = await fetchSignRequest(requestToken);
        isRequestComplete =
          fullRequest?.status === strings.signed ||
          fullRequest?.status === strings.completed;
      } catch (error: any) {
        // Worth logging, not worth blocking on — defaults to the "other
        // signers may still be pending" wording, which is accurate either way.
        console.error(
          "in-person: request-status check after session complete failed",
          error,
        );
      }
      setReturnDeviceBranch(isRequestComplete ? "completed" : "noMoreInPerson");
      return null;
    }
    setPendingNextSession(response);
    setReturnDeviceBranch("nextSigner");
    return response;
  };

  const handleAdvance = async () => {
    if (!requestId) {
      dispatch(endInPersonSession());
      goToRequest();
      return;
    }
    const trimmedCopyEmail = copyEmail.trim();
    if (trimmedCopyEmail && !strings.regex.test(trimmedCopyEmail)) {
      setCopyError(strings.inPerson.copyEmailError);
      return;
    }
    setBusy(true);
    try {
      if (trimmedCopyEmail) {
        try {
          await addInPersonSignerEmail(
            requestToken,
            requestKey,
            session.signerId,
            trimmedCopyEmail,
          );
        } catch (error: any) {
          // 406 → this address is already in use (e.g. another recipient on
          // the envelope), a validation the signer can fix immediately — show
          // it inline and stay on this card rather than advancing past it.
          // Any other failure is best-effort/silent: the signature is already
          // recorded, and holding the device hostage over an unrelated copy-
          // email error that only the sender can fix later helps nobody.
          if (getErrorStatusCode(error) === 406) {
            setCopyError(error?.message || strings.inPerson.copyEmailError);
            return;
          }
          console.error("in-person: saving the copy email failed", error);
        }
      }
      await runNext();
    } catch (error: any) {
      // 406 → the next signer is a blank placeholder needing their details.
      // Land on the same "return device" card the known-signer path shows —
      // the capture dialog itself only opens once the host taps "Start for
      // next signer" there, so every path passes through one decision card
      // before anything else happens.
      const code = getErrorStatusCode(error);
      if (code === 406) {
        setCaptureMsg(error?.message || "");
        setReturnDeviceBranch("nextSigner");
      } else {
        handleErrorMessage(error);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCaptureSubmit = async (name: string, email: string) => {
    setBusy(true);
    try {
      const response = await runNext({ name, email });
      setCaptureOpen(false);
      // The capture dialog already stood in for the "return device" card's
      // confirm step, so a successful submit navigates straight to the
      // signer rather than surfacing that card a second time.
      if (response?.requestToken) {
        dispatch(advanceInPersonSession(response));
        navigateToSigner(response.requestToken, response.requestKey);
      }
    } catch (error: any) {
      const code = getErrorStatusCode(error);
      if (code === 406) {
        setCaptureMsg(error?.message || "");
      } else {
        setCaptureOpen(false);
        handleErrorMessage(error);
      }
    } finally {
      setBusy(false);
    }
  };

  // "End session" (no in-person signers left, request still pending) and
  // "Return to request" (request fully completed) both just close out the
  // client-side session and leave — there is nothing to abandon, since the
  // signer who just finished already completed their turn.
  const handleDone = () => {
    dispatch(endInPersonSession());
    goToRequest();
  };

  // "Start for next signer" on the return-device card: only now does the
  // session actually advance to the next signer and navigate — the pause on
  // that card is the "return the device, hand it to the next signer" beat.
  // `pendingNextSession` is unset when the next signer is still a blank
  // walk-in placeholder (the 406 caught in `handleAdvance`) — that case opens
  // the capture dialog instead, and it's the one that navigates once the host
  // fills in the signer's details.
  const handleStartNextSigner = () => {
    if (pendingNextSession?.requestToken) {
      dispatch(advanceInPersonSession(pendingNextSession));
      navigateToSigner(pendingNextSession.requestToken, pendingNextSession.requestKey);
      return;
    }
    setCaptureOpen(true);
  };

  // `label` defaults to the present-tense "You're signing as" for the
  // pre-sign hand-off card; the completion/return-device cards (past the
  // signer's own turn) pass `signedLabel` instead — reusing the default there
  // would read as if signing were still in progress.
  const renderSignerChip = (label: string = strings.inPerson.signerLabel) => (
    <Box sx={styles.signerChip}>
      <Box component="span" sx={styles.signerChipLabel}>
        {label}
      </Box>
      <Box component="span" sx={styles.signerChipName}>
        {session.signerName}
      </Box>
    </Box>
  );

  const renderHandoff = () => (
    <Box sx={styles.overlay}>
      <Box sx={styles.card}>
        <Box sx={styles.iconCircleOrange}>
          <BorderColorOutlined />
        </Box>
        {/* Each card title is the page heading (WCAG 1.3.1). */}
        <Typography component="h1" sx={styles.title}>
          {`${strings.inPerson.handoffTitle}`}
        </Typography>
        {session.signerName && renderSignerChip()}
        <Typography sx={styles.subtitle}>
          {strings.inPerson.handoffSubtitle}
        </Typography>
        <FormControlLabel
          sx={styles.consentControl}
          control={
            <Checkbox
              id="in_person_signer_consent_checkbox"
              checked={signerConsented}
              onChange={(e: any) => setSignerConsented(e.target.checked)}
              sx={styles.consentCheckbox}
            />
          }
          label={
            <Typography sx={styles.consentLabel}>
              {strings.inPerson.signerConsentLabel}
            </Typography>
          }
        />
        <Box sx={styles.actions}>
          <Button
            disableRipple
            variant="contained"
            disabled={!signerConsented || busy}
            onClick={handleContinueHandoff}
            sx={styles.primaryButton}
          >
            {/* The consent is posted before the document opens, so this button
                covers a real round trip — without the spinner it just greys
                out and the signer taps it again. */}
            {busy ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              strings.inPerson.handoffContinue
            )}
          </Button>
          {!signerConsented && (
            <Typography sx={styles.consentHint}>
              {strings.inPerson.signerConsentHint}
            </Typography>
          )}
          <Button
            disableRipple
            disabled={busy}
            onClick={handleReturnToHost}
            sx={styles.ghostButton}
          >
            {strings.inPerson.returnToHost}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  // The last moment the envelope can still reach this signer directly, so the
  // email field is offered unconditionally rather than only when they gave no
  // email at hand-off — one button (below) both saves it, if filled in, and
  // completes their turn, rather than a separate send-then-continue step.
  const renderCopyField = () => (
    <Box sx={styles.copyBlock}>
      <Typography sx={styles.copyPrompt}>
        {strings.inPerson.copyPrompt}
      </Typography>
      <CustomInput
        label={strings.inPerson.copyEmailLabel}
        placeHolder={strings.inPerson.copyEmailPlaceholder}
        value={copyEmail}
        onChange={(e: any) => {
          setCopyEmail(e.target.value.substring(0, 256));
          setCopyError("");
        }}
        error={copyError}
      />
    </Box>
  );

  const renderCompletion = () => (
    <Box sx={styles.overlay}>
      <Box sx={styles.card}>
        <Box sx={styles.iconCircleSuccess}>
          <CheckCircleOutline />
        </Box>
        <Typography component="h1" sx={styles.title}>
          {`${strings.inPerson.completeTitle}`}
        </Typography>
        {session.signerName &&
          renderSignerChip(strings.inPerson.signedLabel)}
        <Typography sx={styles.subtitle}>
          {strings.inPerson.completeSubtitle}
        </Typography>
        {renderCopyField()}
        <Box sx={styles.actions}>
          <Button
            disableRipple
            variant="contained"
            disabled={busy}
            onClick={handleAdvance}
            sx={styles.primaryButton}
          >
            {busy ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              strings.inPerson.continueNext
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  // Shown right after the completion card, once we know what happens next.
  // Same shell for all three outcomes — only the icon, body copy and single
  // action button change.
  const renderReturnDevice = () => {
    const branch = returnDeviceBranch;
    const isCompleted = branch === "completed";
    const title = isCompleted
      ? strings.inPerson.requestCompletedTitle
      : branch === "noMoreInPerson"
        ? strings.inPerson.returnDeviceNoMoreTitle
        : branch === "nextSigner"
          ? strings.inPerson.returnDeviceNextSignerTitle
          : strings.inPerson.returnDeviceTitle;
    const subtitle =
      branch === "nextSigner"
        ? strings.inPerson.returnDeviceNextSignerSubtitle
        : branch === "noMoreInPerson"
          ? strings.inPerson.returnDeviceNoMoreSubtitle
          : strings.inPerson.requestCompletedSubtitle;
    const actionLabel =
      branch === "nextSigner"
        ? strings.inPerson.startNextSigner
        : branch === "noMoreInPerson"
          ? strings.inPerson.endSession
          : strings.inPerson.returnToRequest;
    const onAction =
      branch === "nextSigner" ? handleStartNextSigner : handleDone;

    return (
      <Box sx={styles.overlay}>
        <Box sx={styles.card}>
          <Box sx={styles.iconCircleSuccess}>
            {isCompleted ? <VerifiedOutlined /> : <CheckCircleOutline />}
          </Box>
          <Typography component="h1" sx={styles.title}>{title}</Typography>
          <Typography sx={styles.subtitle}>{subtitle}</Typography>
          <Box sx={styles.actions}>
            <Button
              disableRipple
              variant="contained"
              onClick={onAction}
              sx={styles.primaryButton}
            >
              {actionLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderReturnBar = () => (
    <>
      {/* Desktop (`md`+): the full name + button pill. */}
      <Box sx={styles.bar}>
        <Box sx={styles.barMeta}>
          <Box component="span" sx={styles.barLabel}>
            {strings.inPerson.sessionBadge}
          </Box>
          <Box component="span" sx={styles.barName}>
            {session.signerName}
          </Box>
        </Box>
        <Button
          disableRipple
          disabled={busy}
          onClick={handleReturnToHost}
          sx={styles.barButton}
        >
          {strings.inPerson.returnToHost}
        </Button>
      </Box>
      {/* Phone/tablet: no room anywhere on this screen for a pill without
          sitting on top of something (sticky header up top, Finish button
          and an AI FAB down bottom) — a small top-right icon instead. */}
      {/* describeChild: the IconButton carries its own label, so the Tooltip
          must not also put an aria-label on the bare span (WCAG 4.1.2). */}
      <Tooltip title={strings.inPerson.returnToHost} arrow describeChild>
        {/* A disabled IconButton swallows hover events, so Tooltip needs a
            plain wrapper to keep listening — it carries no positioning of
            its own, that's all on `barCompact` (fixed) below. */}
        <span>
          <IconButton
            disableRipple
            disabled={busy}
            onClick={handleReturnToHost}
            aria-label={strings.inPerson.returnToHost}
            sx={styles.barCompact}
          >
            <KeyboardReturnOutlined />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  const renderOverlay = () => {
    if (returnDeviceBranch) return renderReturnDevice();
    if (isSignedView) return renderCompletion();
    if (!session.handoffAcknowledged) return renderHandoff();
    return renderReturnBar();
  };

  return (
    <>
      {children}
      {renderOverlay()}
      <InPersonHandoffDialog
        open={captureOpen}
        requestId={requestId}
        hostToken={hostToken || undefined}
        hostKey={hostToken ? hostKey : undefined}
        message={captureMsg}
        loading={busy}
        onCancel={() => setCaptureOpen(false)}
        onSubmit={handleCaptureSubmit}
      />
      <ConfirmModal
        open={leaveConfirmOpen}
        onClose={handleCancelLeave}
        onCancel={handleCancelLeave}
        title={strings.inPerson.leaveSessionTitle}
        message={strings.inPerson.leaveSessionMessage}
        tone="destructive"
        confirmLabel={strings.inPerson.leaveSessionConfirm}
        cancelLabel={strings.inPerson.leaveSessionCancel}
        onConfirm={handleConfirmLeave}
        loading={leaveBusy}
      />
    </>
  );
};

export default InPersonSessionShell;
